import { useState, useCallback, useMemo, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@learn-me/ui";
import { Save, Loader2, Volume2, AlertTriangle, Check } from "lucide-react";
import { MiniRichTextEditor } from "@/components/editor/MiniRichTextEditor";
import { adminApi, type ChapterSection } from "../api/adminApi";
import toast from "react-hot-toast";

interface BulkSectionEditorProps {
  chapterId: string;
  sections: ChapterSection[];
  onSave: () => void;
  onCancel: () => void;
}

interface EditedSection {
  title: string;
  content: string;
}

// Helper to count words in HTML content
function countWords(html: string): number {
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return 0;
  return text.split(" ").length;
}

// Get word count status
function getWordCountStatus(count: number): "ok" | "warning" | "error" {
  if (count < 150) return "ok"; // Could add more content
  if (count <= 300) return "ok"; // Perfect range
  if (count <= 400) return "warning"; // A bit long
  return "error"; // Too long
}

export function BulkSectionEditor({
  chapterId,
  sections,
  onSave,
  onCancel,
}: BulkSectionEditorProps) {
  const queryClient = useQueryClient();

  // Initialize edited sections from props
  const [editedSections, setEditedSections] = useState<
    Map<string, EditedSection>
  >(
    () =>
      new Map(
        sections.map((s) => [s.id, { title: s.title, content: s.content }]),
      ),
  );

  // Sync when sections prop changes
  useEffect(() => {
    setEditedSections(
      new Map(
        sections.map((s) => [s.id, { title: s.title, content: s.content }]),
      ),
    );
  }, [sections]);

  // Track which sections have changes
  const changedSectionIds = useMemo(() => {
    const changed: string[] = [];
    for (const section of sections) {
      const edited = editedSections.get(section.id);
      if (
        edited &&
        (edited.title !== section.title || edited.content !== section.content)
      ) {
        changed.push(section.id);
      }
    }
    return changed;
  }, [sections, editedSections]);

  const hasChanges = changedSectionIds.length > 0;

  // Update a single section field
  const updateSection = useCallback(
    (sectionId: string, field: "title" | "content", value: string) => {
      setEditedSections((prev) => {
        const newMap = new Map(prev);
        const current = newMap.get(sectionId);
        if (current) {
          newMap.set(sectionId, { ...current, [field]: value });
        }
        return newMap;
      });
    },
    [],
  );

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const promises: Promise<unknown>[] = [];

      for (const sectionId of changedSectionIds) {
        const edited = editedSections.get(sectionId);
        if (edited) {
          promises.push(
            adminApi.updateSection(sectionId, {
              title: edited.title,
              content: edited.content,
            }),
          );
        }
      }

      await Promise.all(promises);
      return changedSectionIds.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      toast.success(`${count} secciones guardadas`);
      onSave();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al guardar secciones",
      );
    },
  });

  const handleSaveAll = () => {
    saveMutation.mutate();
  };

  return (
    <div className="space-y-4">
      {/* Sticky header with save button */}
      <div className="sticky top-0 z-10 bg-white border-b -mx-4 px-4 py-3 flex justify-between items-center shadow-sm">
        <div>
          <h3 className="font-semibold text-gray-900">Edicion Masiva</h3>
          <p className="text-sm text-gray-500">
            Editando {sections.length} secciones
            {hasChanges && (
              <span className="ml-2 text-amber-600">
                ({changedSectionIds.length} con cambios)
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saveMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSaveAll}
            disabled={!hasChanges || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Guardar Todo ({changedSectionIds.length})
          </Button>
        </div>
      </div>

      {/* Sections list */}
      <div className="space-y-6">
        {sections.map((section, idx) => {
          const edited = editedSections.get(section.id);
          const wordCount = edited ? countWords(edited.content) : 0;
          const status = getWordCountStatus(wordCount);
          const hasChange = changedSectionIds.includes(section.id);

          return (
            <div
              key={section.id}
              className={`border rounded-lg p-4 bg-white transition-colors ${
                hasChange
                  ? "border-amber-300 bg-amber-50/30"
                  : "border-gray-200"
              }`}
            >
              {/* Section header */}
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    hasChange
                      ? "bg-amber-100 text-amber-700"
                      : "bg-primary-100 text-primary-700"
                  }`}
                >
                  {idx + 1}
                </span>
                <Input
                  value={edited?.title || ""}
                  onChange={(e) =>
                    updateSection(section.id, "title", e.target.value)
                  }
                  className="flex-1 font-medium"
                  placeholder="Titulo de la seccion"
                />
                {/* Audio indicator */}
                {section.audioUrl ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    <Volume2 className="h-3 w-3" />
                    Audio
                  </span>
                ) : null}
                {/* Change indicator */}
                {hasChange && (
                  <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                    <AlertTriangle className="h-3 w-3" />
                    Modificado
                  </span>
                )}
              </div>

              {/* Content editor */}
              <MiniRichTextEditor
                content={edited?.content || ""}
                onChange={(html) => updateSection(section.id, "content", html)}
                placeholder="Contenido de la seccion (150-300 palabras)..."
              />

              {/* Word count and status */}
              <div className="mt-2 flex justify-between text-xs">
                <span
                  className={`flex items-center gap-1 ${
                    status === "ok"
                      ? "text-gray-400"
                      : status === "warning"
                        ? "text-amber-500"
                        : "text-red-500"
                  }`}
                >
                  {wordCount} palabras
                  {status === "ok" && wordCount >= 150 && wordCount <= 300 && (
                    <Check className="h-3 w-3 text-green-500" />
                  )}
                </span>
                {status === "warning" && (
                  <span className="text-amber-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Un poco largo para un slide
                  </span>
                )}
                {status === "error" && (
                  <span className="text-red-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Muy largo - considera dividir en 2 secciones
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom save button for long lists */}
      {sections.length > 3 && (
        <div className="sticky bottom-0 bg-white border-t -mx-4 px-4 py-3 flex justify-end shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
          <Button
            type="button"
            onClick={handleSaveAll}
            disabled={!hasChanges || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Guardar Todo ({changedSectionIds.length})
          </Button>
        </div>
      )}
    </div>
  );
}
